import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  aiFeatureConfigsApi,
  type PlatformAiFeatureConfig,
  type UpdatePlatformAiFeatureConfigInput,
} from '@/modules/platform/services/aiFeatureConfigsApi';

/**
 * Silent auto-save for platform AI feature configs (Phase 39.2).
 *
 * Matches `usePreference.ts` (DEBOUNCE_MS = 750) — native setTimeout + ref,
 * optimistic React Query cache update, rollback on error. No toasts —
 * success is silent, failure surfaces inline via `status: 'error'`
 * (`feedback_silent_background_sync.md`).
 *
 * Per-feature debounce slot — editing temperature for `description` while
 * simultaneously changing max_tokens for `image-analysis` produces two
 * independent PATCHes. Each instance of `usePatchPlatformAiFeatureConfig`
 * owns its own timer ref for the feature it was constructed with.
 *
 * Cleanup flushes any pending mutation on unmount so tab-switch doesn't drop
 * changes (research pitfall 7).
 */

const DEBOUNCE_MS = 750;

export type FeatureSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function usePlatformAiFeatureConfigs() {
  return useQuery({
    queryKey: ['platform', 'ai', 'feature-configs'],
    queryFn:  aiFeatureConfigsApi.list,
  });
}

/**
 * Auto-save-aware mutation for a single feature row. Optimistic UI update +
 * 750 ms debounced server PATCH + rollback on error. No toasts. Mirrors
 * `usePreference.ts`'s canonical silent-sync pattern.
 *
 * Returns `{save, status, lastSavedAt}`:
 *   - save(patch)        — call with whichever subset of {model, temperature,
 *                          max_tokens} changed. Merges with any already-pending
 *                          partial patch so two quick edits produce one PATCH.
 *   - status             — 'idle' | 'saving' | 'saved' | 'error' — render as
 *                          an inline pill per section.
 *   - lastSavedAt        — ms epoch of the last successful save; feeds the
 *                          "Saved 3s ago" indicator.
 */
export function usePatchPlatformAiFeatureConfig(feature: string) {
  const qc = useQueryClient();
  const [status, setStatus]             = useState<FeatureSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt]   = useState<number | null>(null);
  const timerRef                         = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef                       = useRef<UpdatePlatformAiFeatureConfigInput | null>(null);
  const snapshotRef                      = useRef<PlatformAiFeatureConfig[] | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: (input: UpdatePlatformAiFeatureConfigInput) =>
      aiFeatureConfigsApi.update(feature, input),
  });

  // Flush any pending save on unmount so tab-switch doesn't drop changes
  // (research pitfall 7). Fire-and-forget — we're leaving anyway.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (pendingRef.current) {
        void aiFeatureConfigsApi.update(feature, pendingRef.current).catch(() => {
          // Silent — per feedback_silent_background_sync.md.
        });
      }
    };
  }, [feature]);

  const save = useCallback((patch: UpdatePlatformAiFeatureConfigInput) => {
    // Optimistic cache update so the UI reflects the new value immediately.
    const prev = qc.getQueryData<PlatformAiFeatureConfig[]>(['platform', 'ai', 'feature-configs']);
    snapshotRef.current = prev;
    if (prev) {
      qc.setQueryData<PlatformAiFeatureConfig[]>(
        ['platform', 'ai', 'feature-configs'],
        prev.map((row) => row.feature === feature ? { ...row, ...patch } : row),
      );
    }

    // Merge with any already-pending patch so partial changes don't clobber.
    pendingRef.current = { ...(pendingRef.current ?? {}), ...patch };
    setStatus('saving');

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const body = pendingRef.current!;
      pendingRef.current = null;
      timerRef.current   = null;

      mutation.mutate(body, {
        onSuccess: (row) => {
          // Sync server-truth back into the cache (covers coercion/defaults).
          qc.setQueryData<PlatformAiFeatureConfig[]>(
            ['platform', 'ai', 'feature-configs'],
            (curr) => curr ? curr.map((r) => r.feature === feature ? row : r) : curr,
          );
          setStatus('saved');
          setLastSavedAt(Date.now());
        },
        onError: () => {
          // Rollback optimistic update + surface error inline.
          if (snapshotRef.current) {
            qc.setQueryData(['platform', 'ai', 'feature-configs'], snapshotRef.current);
          }
          setStatus('error');
        },
      });
    }, DEBOUNCE_MS);
  }, [feature, mutation, qc]);

  return { save, status, lastSavedAt };
}
